import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import financeService from '../services/financeService';
import api from '../services/api';
import admissionService from '../services/admissionService';
import studentService from '../services/studentService';

/* ─────────────────────────────────────────────
   HELPERS
─────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return '';
  try {
    const d = new Date(str);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return str; }
};

// Same logic as StudentAnalytics — parse installments into schedule items
// Status is date-based: if due date is in the past → 'paid', else → 'pending'
const parseInstallments = (admission, totalFees) => {
  if (!admission) return [];

  // 1. Use normalized installments array from admissionService
  let raw = admission.installments;

  // 2. Fallback: build from flat inst1..inst4 fields
  if (!raw || !Array.isArray(raw) || raw.length === 0) {
    raw = [admission.inst1, admission.inst2, admission.inst3, admission.inst4]
      .filter(d => d != null && d !== '');
  }
  if (!raw || raw.length === 0) return [];

  const total     = totalFees || 0;
  const count     = raw.length;
  const perSlot   = count > 0 ? Math.floor(total / count) : 0;
  const remainder = total - perSlot * (count - 1);
  const today     = new Date();
  today.setHours(0, 0, 0, 0); // compare date only, not time

  return raw.map((item, idx) => {
    const isObj   = item && typeof item === 'object';
    const dateStr = isObj ? (item.date || item.dueDate || '') : item;

    // Parse amount — use provided value, or distribute total evenly
    const amount = isObj && item.amount
      ? Number(item.amount)
      : (idx === count - 1 ? remainder : perSlot);

    // Parse status — use provided status, or derive from whether due date is past
    let status = 'pending';
    if (isObj && item.status) {
      status = item.status;
    } else if (dateStr) {
      // Parse ISO date string safely (avoid UTC-vs-local timezone issues)
      const parts = String(dateStr).split('T')[0].split('-');
      if (parts.length === 3) {
        const due = new Date(
          parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])
        );
        status = due < today ? 'paid' : 'pending';
      }
    }

    return {
      date:   dateStr ? formatDate(dateStr) : `Instalment ${idx + 1}`,
      amount,
      status,
    };
  });
};

/* ─────────────────────────────────────────────
   COLLECT PAYMENT MODAL
─────────────────────────────────────────────── */
// Fee head options
const FEE_HEAD_LIST = [
  { key:'accommodation', label:'Accommodation Charges', icon:'🏠' },
  { key:'food',          label:'Food Charges',          icon:'🍽️' },
  { key:'maintenance',   label:'Maintenance Charges (Fix per Annum)', icon:'🔧' },
  { key:'deposit',       label:'Refundable Deposit',    icon:'🔒' },
  { key:'prepaidCard',   label:'Prepaid Card Load',     icon:'💳' },
  { key:'twoStepPremium', label:'Two-Step Payment Premium', icon:'⚡' },
  { key:'other',         label:'Other / Miscellaneous', icon:'📋', disabled:true },
];

const CollectPaymentModal = ({ admissionId, studentName, balance, detail, onClose, onSuccess }) => {
  // Normalise a fee-head label so "Maintenance Charges (Fix per Annum)" and
  // "Maintenance Charges" are treated as the same head.
  const normLabel = (s) => String(s || '').toLowerCase().replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();

  // Compute already paid per head from payment history remarks (keyed by normalised label)
  const alreadyPaid = React.useMemo(() => {
    const map = {};
    (detail?.history || []).forEach(r => {
      const m = (r.remarks||'').match(/^\[([^\]]+)\]/);
      if (m) {
        const key = normLabel(m[1]);
        map[key] = (map[key] || 0) + (r.amount || 0);
      }
    });
    return map;
  }, [detail]);

  // What was charged at admission, per fee-head key
  const chargedAmts = React.useMemo(() => ({
    accommodation:  detail?.hostelFees         || 0,
    food:           detail?.foodCharges        || 0,
    maintenance:    detail?.maintenanceCharges || 0,
    deposit:        detail?.refundableDeposit  || 0,
    prepaidCard:    detail?.prepaidCard        || 0,
    twoStepPremium: detail?.mattressCharge     || 0,
    other:          0,
  }), [detail]);

  // Pending = charged - already paid (matched by normalised label, not a hardcoded string)
  const pendingAmts = React.useMemo(() => {
    const out = {};
    FEE_HEAD_LIST.forEach(h => {
      const paid = alreadyPaid[normLabel(h.label)] || 0;
      out[h.key] = Math.max(0, (chargedAmts[h.key] || 0) - paid);
    });
    return out;
  }, [chargedAmts, alreadyPaid]);

  const isSuperOwner = true; // ✅ All roles can edit fee amounts

  // Two-Step Payment Premium is the ONLY optional head (chosen at confirm admission).
  // 0 → never charged for this student → show "Not applicable", not "✓ Paid".
  // All other heads are compulsory, so ₹0 pending genuinely means fully paid.
  const twoStepCharged = (detail?.mattressCharge || 0) > 0;

  const [heads,   setHeads]   = useState(() =>
    FEE_HEAD_LIST.map(h => ({ ...h, selected:false,
      disabled: h.disabled || (h.key === 'twoStepPremium' && !twoStepCharged),
      amount: pendingAmts[h.key] || '' }))
  );

  // Sync amounts + disabled state when pendingAmts changes
  React.useEffect(() => {
    setHeads(prev => prev.map(h => ({
      ...h,
      disabled: (h.key === 'other') || (h.key === 'twoStepPremium' && !twoStepCharged),
      amount: pendingAmts[h.key] ?? h.amount,
    })));
  }, [pendingAmts, twoStepCharged]);
  const [mode,    setMode]    = useState('CASH');
  const [txnId,   setTxnId]   = useState('');
  const [date,    setDate]    = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const inp = {
    width:'100%', padding:'10px 12px', border:'2px solid #E8E4DF',
    borderRadius:6, fontSize:14, fontFamily:'Work Sans, sans-serif',
    outline:'none', boxSizing:'border-box',
  };

  const toggleHead = (key) => setHeads(hs => hs.map(h => h.key===key ? { ...h, selected:!h.selected } : h));
  const setAmt     = (key, val) => { setHeads(hs => hs.map(h => h.key===key ? { ...h, amount:val } : h)); };
  const selected   = heads.filter(h => h.selected);
  const totalAmt   = selected.reduce((s,h) => s + (parseFloat(h.amount)||0), 0);

  const handleCollect = async () => {
    if (selected.length === 0) { setError('Please select at least one fee head'); return; }
    const invalid = selected.find(h => !h.amount || parseFloat(h.amount) <= 0);
    if (invalid) { setError(`Enter amount for ${invalid.label}`); return; }
    if (totalAmt > balance) { setError(`Total ₹${totalAmt.toLocaleString('en-IN')} exceeds balance ₹${Number(balance).toLocaleString('en-IN')}`); return; }
    setSaving(true); setError('');
    try {
      // Create one payment record per fee head
      for (const h of selected) {
        await financeService.collectPayment({
          admissionId,
          amount:        parseFloat(h.amount),
          paymentMode:   mode,
          transactionId: txnId || null,
          paymentDate:   date,
          remarks:       `[${h.label}]${remarks ? ' ' + remarks : ''}`,
        });
      }

      // ✅ If prepaid card fee was collected → topup PayCard wallet + activate it
      const prepaidPayment = selected.find(h =>
        h.key === 'prepaidCard' || h.label?.toLowerCase().includes('prepaid')
      );
      if (prepaidPayment && parseFloat(prepaidPayment.amount) > 0) {
        try {
          // ✅ Use THIS admission's own pay card id.
          // Do NOT look up by studentId: the backend matches admission_number FIRST,
          // and the student_id / admission_number series are offset by one — which
          // credited the previous student's card. detail.paycard.cardId is exact.
          const payCardId = detail?.paycard?.cardId;
          if (payCardId) {
            await api.post('/paycards/topup', {
              payCardId,
              amount:      parseFloat(prepaidPayment.amount),
              paymentMode: mode,
              referenceId: txnId || null,
              operatorName:'Admin',
              description: 'Prepaid Card Load — Fee Payment',
            });
            console.log('✅ PayCard topped up:', payCardId, prepaidPayment.amount);
          } else {
            console.warn('⚠ No pay card exists for this admission — top-up skipped');
          }
        } catch (pcErr) {
          console.warn('PayCard topup failed (non-critical):', pcErr.message);
        }
      }

      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Payment failed. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={onClose}>
      <div style={{ background:'white', borderRadius:12, width:'100%', maxWidth:520,
        maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Fixed Header */}
        <div style={{ padding:'24px 28px 16px', borderBottom:'1px solid #E8E4DF', flexShrink:0 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, color:'#1B3B5F', marginBottom:4 }}>Collect Payment</div>
          <div style={{ color:'#6B6B6B', fontSize:13 }}>{studentName} — Balance: ₹{Number(balance||0).toLocaleString('en-IN')}</div>

        </div>

        {/* Scrollable Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 28px' }}>

          {error && <div style={{ background:'#F8D7DA', border:'1px solid #F5C6CB', borderRadius:6,
            padding:'10px 14px', marginBottom:14, color:'#721C24', fontSize:13 }}>{error}</div>}

          {/* Multi-select Fee Heads */}
          <div style={{ marginBottom:16 }}>
            <label style={{ display:'block', fontWeight:700, fontSize:11, textTransform:'uppercase',
              letterSpacing:1, color:'#6B6B6B', marginBottom:8 }}>
              Select Fee Head(s) * <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0, color:'#9B9B9B' }}>(multiple allowed)</span>
            </label>
            <div style={{ border:'1.5px solid #E8E4DF', borderRadius:10, overflow:'hidden' }}>
              {heads.map((h, i) => (
                <div key={h.key} style={{
                  display:'flex', alignItems:'center', gap:10, padding:'11px 14px',
                  borderBottom: i < heads.length-1 ? '1px solid #F0EDE8' : 'none',
                  background: h.selected ? '#F0F6FF' : '#fff', transition:'background 0.15s',
                }}>
                  {/* Checkbox */}
                  <div onClick={() => !h.disabled && toggleHead(h.key)} style={{
                    width:20, height:20, borderRadius:5, flexShrink:0,
                    cursor: h.disabled ? 'not-allowed' : 'pointer',
                    border:`2px solid ${h.disabled ? '#E8E4DF' : h.selected ? '#1B3B5F' : '#C4BEB8'}`,
                    background: h.disabled ? '#F0EDE8' : h.selected ? '#1B3B5F' : '#fff',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    opacity: h.disabled ? 0.5 : 1,
                  }}>
                    {h.selected && !h.disabled && <span style={{ color:'#fff', fontSize:11, fontWeight:800, lineHeight:1 }}>✓</span>}
                  </div>
                  <span style={{ fontSize:17, flexShrink:0 }}>{h.icon}</span>
                  <div style={{ flex:1, cursor: h.disabled ? 'not-allowed' : 'pointer', opacity: h.disabled ? 0.5 : 1 }} onClick={() => !h.disabled && toggleHead(h.key)}>
                    <span style={{ fontSize:13, fontWeight:600, color: h.selected ? '#1B3B5F' : '#6B6B6B' }}>
                      {h.label}
                    </span>
                    {/* Optional head never charged for this student → not applicable */}
                    {h.key === 'twoStepPremium' && !twoStepCharged && (
                      <span style={{ fontSize:11, fontWeight:600, color:'#9B9B9B', marginLeft:6 }}>Not applicable</span>
                    )}
                    {/* ✅ Show remaining balance per fee head */}
                    {h.key !== 'other' && !(h.key === 'twoStepPremium' && !twoStepCharged) && pendingAmts[h.key] > 0 && (
                      <span style={{ fontSize:11, fontWeight:600, color:'#E85D4A', marginLeft:6 }}>
                        (Bal: ₹{Number(pendingAmts[h.key]).toLocaleString('en-IN')})
                      </span>
                    )}
                    {h.key !== 'other' && !(h.key === 'twoStepPremium' && !twoStepCharged) && pendingAmts[h.key] === 0 && (
                      <span style={{ fontSize:11, fontWeight:600, color:'#22c55e', marginLeft:6 }}>✓ Paid</span>
                    )}
                  </div>
                  {/* Amount field — only when selected */}
                  {h.selected && (
                    <div style={{ display:'flex', alignItems:'center', gap:5, flexShrink:0 }}>
                      <span style={{ fontSize:13, color:'#6B6B6B', fontWeight:600 }}>₹</span>
                      <input type="number" value={h.amount}
                        onChange={e => setAmt(h.key, e.target.value)}
                        placeholder="0"
                        style={{ width:100, padding:'6px 10px', border:'2px solid #1B3B5F',
                          borderRadius:6, fontSize:13, fontWeight:700, fontFamily:'monospace',
                          outline:'none', textAlign:'right', color:'#1B3B5F', boxSizing:'border-box' }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Total */}
            {selected.length > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 14px',
                background:'#1B3B5F', borderRadius:'0 0 10px 10px', marginTop:-1 }}>
                <span style={{ fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.8)' }}>
                  Total ({selected.length} head{selected.length>1?'s':''})
                </span>
                <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:700, color:'#D4AF37' }}>
                  ₹{totalAmt.toLocaleString('en-IN')}
                </span>
              </div>
            )}
          </div>

          {/* Payment Mode */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:6 }}>Payment Mode</label>
            <select style={{ ...inp, cursor:'pointer' }} value={mode} onChange={e => setMode(e.target.value)}>
              <option value="CASH">Cash</option>
              <option value="CASH_DEPOSIT">Cash Deposit</option>
              <option value="UPI">UPI</option>
              <option value="CARD">Card</option>
              <option value="CHEQUE">Cheque</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
            </select>
          </div>

          {/* Transaction ID */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:6 }}>Transaction ID / Reference</label>
            <input type="text" style={inp} placeholder="Optional" value={txnId} onChange={e => setTxnId(e.target.value)} />
          </div>

          {/* Date */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:6 }}>Payment Date</label>
            <input type="date" style={inp} value={date} onChange={e => setDate(e.target.value)} />
          </div>

          {/* Remarks */}
          <div style={{ marginBottom:8 }}>
            <label style={{ display:'block', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:'#6B6B6B', marginBottom:6 }}>Remarks (Optional)</label>
            <input type="text" style={inp} placeholder="Any additional notes" value={remarks} onChange={e => setRemarks(e.target.value)} />
          </div>
        </div>

        {/* Fixed Footer with Cancel + Collect */}
        <div style={{ padding:'14px 28px 20px', borderTop:'1px solid #E8E4DF', flexShrink:0,
          display:'flex', gap:12 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:'13px', background:'#E8E4DF', color:'#2C2C2C',
              border:'none', borderRadius:8, fontWeight:700, fontSize:13,
              textTransform:'uppercase', letterSpacing:1, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={handleCollect} disabled={saving}
            style={{ flex:2, padding:'13px', background: saving ? '#aaa' : '#2D5F4F',
              color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13,
              textTransform:'uppercase', letterSpacing:1,
              cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Processing…' : `Collect${totalAmt > 0 ? ' ₹'+totalAmt.toLocaleString('en-IN') : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};


const StudentFeeDetails = ({ onNavigate, onLogout, studentId }) => {
  const [detail,     setDetail]     = useState(null);
  const [admission,  setAdmission]  = useState(null); // raw admission for installments
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [showModal,  setShowModal]  = useState(false);

  const admissionId = studentId;

  const loadDetail = () => {
    if (!admissionId) { setError('No admission ID provided'); setLoading(false); return; }
    setLoading(true);

    // Fetch finance detail + raw admission in parallel
    Promise.all([
      financeService.getStudentFeeDetail(admissionId),
      admissionService.getById(admissionId).catch(() => null),
    ])
      .then(([feeDetail, rawAdmission]) => {
        setDetail(feeDetail);
        setAdmission(rawAdmission);
        setError('');
      })
      .catch(() => setError('Could not load student fee details'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadDetail(); }, [admissionId]); // eslint-disable-line

  const cardStyle = { background: 'white', borderRadius: 12, padding: 30, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' };
  const cardHeaderStyle = { display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 20, marginBottom: 25, borderBottom: '2px solid #E8E4DF' };

  /* ── Loading ── */
  if (loading) return (
    <div className="app-layout">
      <Sidebar activeItem="finance" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content" style={{ background: '#F8F6F3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 18, color: '#6B6B6B' }}>Loading fee details…</div>
      </main>
    </div>
  );

  /* ── Error ── */
  if (error || !detail) return (
    <div className="app-layout">
      <Sidebar activeItem="finance" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content" style={{ background: '#F8F6F3', padding: 40 }}>
        <div style={{ background: '#F8D7DA', border: '1px solid #F5C6CB', borderRadius: 8, padding: '20px 25px', color: '#721C24' }}>
          ⚠️ {error || 'Student not found'}
          <button onClick={() => onNavigate('finance')} style={{ marginLeft: 20, padding: '8px 16px', background: '#1B3B5F', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>← Back to Finance</button>
        </div>
      </main>
    </div>
  );

  /* ── Compute installment schedule ── */
  // Try API response first (detail.schedule), then fall back to parsing raw admission
  let schedule = detail.schedule && detail.schedule.length > 0
    ? detail.schedule
    : parseInstallments(admission, detail.totalFees);

  /* ── Fee breakdown rows ── */
  const fmtAmt = (n) => '₹' + Number(n||0).toLocaleString('en-IN');
  const roomTypeLabel = {
    '4sharing': '4 Sharing', '2sharing': '2 Sharing (Twin)',
    '2sharingPremium': '2 Sharing (Premium)', 'private': 'Single / Private',
  };
  const feeBreakdown = [
    ...(detail.roomType ? [{ label: 'Room Type',    amount: roomTypeLabel[detail.roomType] || detail.roomType, type: 'normal' }] : []),
    ...(detail.months   ? [{ label: 'Months Billed', amount: detail.months + (detail.months === 1 ? ' month' : ' months'), type: 'normal' }] : []),
    { label: 'Accommodation Charges',         amount: fmtAmt(detail.hostelFees),          type: 'normal'  },
    { label: 'Food Charges',                  amount: fmtAmt(detail.foodCharges),          type: 'normal'  },
    { label: 'Maintenance Fix Charges',       amount: fmtAmt(detail.maintenanceCharges),   type: 'normal'  },
    { label: 'Refundable Security Deposit',   amount: fmtAmt(detail.refundableDeposit),    type: 'normal'  },
    { label: 'Prepaid Card Load',             amount: fmtAmt(detail.prepaidCard),          type: 'normal'  },
    ...(detail.mattressCharge > 0 ? [{ label: 'Two-Step Payment Premium', amount: fmtAmt(detail.mattressCharge), type: 'normal' }] : []),
    ...(detail.gst > 0 ? [{ label: 'GST',    amount: fmtAmt(detail.gst),                  type: 'normal' }] : []),
    { label: 'Total Payable',                 amount: detail.totalFmt,                     type: 'total'   },
    { label: 'Amount Paid',                   amount: detail.paidFmt,                      type: 'paid'    },
    { label: 'Balance Due',                   amount: detail.balanceFmt,                   type: 'pending' },
  ];

  /* ── PayCard data: real if available, hardcoded placeholder otherwise ── */
  const pc = detail.paycard || {
    balance: 0, totalTopup: 0, totalSpent: 0, transactionCount: 0,
    status: 'inactive', isPlaceholder: true,
  };

  return (
    <div className="app-layout">
      <Sidebar activeItem="finance" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content" style={{ background: '#F8F6F3' }}>

        {/* ── TOP BAR ── */}
        <div style={{ background: 'white', padding: '20px 40px', borderBottom: '1px solid #E8E4DF' }}>
          <div style={{ fontSize: 13, color: '#6B6B6B', marginBottom: 10 }}>
            <span style={{ color: '#D4AF37', cursor: 'pointer' }} onClick={() => onNavigate('finance')}>Finance</span>
            {' / '}
            <span style={{ color: '#D4AF37', cursor: 'pointer' }} onClick={() => onNavigate('finance')}>Admission Fees</span>
            {' / '}
            <span>Student Fee Details</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 400, color: '#1B3B5F', margin: 0 }}>
              {detail.studentName}
            </h1>
            <div style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #C19A2E 100%)', color: '#1B3B5F', padding: '10px 25px', borderRadius: 25, fontSize: 14, fontWeight: 700, letterSpacing: 1 }}>
              Student ID: {detail.admissionNumber}
            </div>
          </div>
        </div>

        <div style={{ padding: 40, maxWidth: 1400, margin: '0 auto' }}>

          {/* ── STUDENT INFO CARD ── */}
          <div style={{ ...cardStyle, display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 30, alignItems: 'center', marginBottom: 30 }}>
            <div style={{ width:100, height:100, borderRadius:'50%', border:'4px solid #D4AF37', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', background:'#1B3B5F' }}>
              {detail.photo
                ? <img
                    src={studentService.getPhotoUrl(detail.studentId)}
                    alt="Student"
                    style={{ width:'100%', height:'100%', objectFit:'cover' }}
                    onError={e => { e.target.style.display='none'; e.target.parentNode.querySelector('.photo-fallback').style.display='flex'; }}
                  />
                : null}
              <div className="photo-fallback" style={{ display: detail.photo ? 'none' : 'flex', width:'100%', height:'100%', alignItems:'center', justifyContent:'center', fontSize:36, color:'white', fontWeight:700 }}>
                {detail.studentName?.[0]?.toUpperCase() || '👤'}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
              {[
                { label: 'Room Number',    val: detail.roomCode      },
                { label: 'Admission Date', val: detail.admissionDate },
                { label: 'Stream',         val: detail.stream        },
                { label: 'Mobile',         val: detail.mobile        },
                { label: 'Email',          val: detail.email         },
                { label: 'Valid Till',     val: detail.validTill     },
              ].map(f => (
                <div key={f.label} style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 5 }}>{f.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#2C2C2C' }}>{f.val || '—'}</div>
                </div>
              ))}
            </div>
            <div>
              {detail.balance > 0 && (
                <button onClick={() => setShowModal(true)}
                  style={{ padding: '15px 30px', background: '#2D5F4F', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => { e.target.style.background = '#25503F'; e.target.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.target.style.background = '#2D5F4F'; e.target.style.transform = 'none'; }}>
                  💰 Collect Payment
                </button>
              )}
            </div>
          </div>

          {/* ── FEE BREAKDOWN + PAYMENT SCHEDULE ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30, marginBottom: 30 }}>

            {/* Fee Breakdown */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: 24 }}>💳</span>
                <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#1B3B5F', margin: 0 }}>Fee Breakdown</h2>
              </div>
              {feeBreakdown.map((f, i) => {
                if (f.type === 'total') return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8F6F3', padding: '20px 15px', marginTop: 15, borderRadius: 8, border: '2px solid #D4AF37' }}>
                    <span style={{ fontSize: 16, color: '#1B3B5F', fontWeight: 700 }}>{f.label}</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: '#D4AF37' }}>{f.amount}</span>
                  </div>
                );
                if (f.type === 'paid') return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0 15px 15px', borderLeft: '4px solid #2D5F4F', marginTop: 30 }}>
                    <span style={{ fontSize: 14, color: '#6B6B6B', fontWeight: 600 }}>{f.label}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#2D5F4F' }}>{f.amount}</span>
                  </div>
                );
                if (f.type === 'pending') return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0 15px 15px', borderLeft: `4px solid ${detail.balance > 0 ? '#DC3545' : '#2D5F4F'}`, marginTop: 8 }}>
                    <span style={{ fontSize: 14, color: '#6B6B6B', fontWeight: 600 }}>{f.label}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: detail.balance > 0 ? '#DC3545' : '#2D5F4F' }}>{f.amount}</span>
                  </div>
                );
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderBottom: '1px solid #E8E4DF' }}>
                    <span style={{ fontSize: 14, color: '#6B6B6B', fontWeight: 600 }}>{f.label}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#2C2C2C' }}>{f.amount}</span>
                  </div>
                );
              })}
            </div>

            {/* ── PAYMENT SCHEDULE ── */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={{ fontSize: 24 }}>📅</span>
                <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#1B3B5F', margin: 0 }}>Payment Schedule</h2>
              </div>

              {schedule.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 20px', color: '#6B6B6B' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>No installment schedule set</div>
                  <div style={{ fontSize: 13 }}>Installment dates can be added from the student's admission record.</div>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* vertical timeline line */}
                  <div style={{ position: 'absolute', left: 20, top: 8, bottom: 8, width: 2, background: '#E8E4DF' }} />
                  {schedule.map((p, i) => (
                    <div key={i} style={{ position: 'relative', paddingLeft: 60, paddingBottom: i < schedule.length - 1 ? 28 : 0 }}>
                      {/* dot */}
                      <div style={{
                        position: 'absolute', left: 13, top: 4,
                        width: 16, height: 16, borderRadius: '50%',
                        border: `3px solid ${p.status === 'paid' ? '#2D5F4F' : '#F59E0B'}`,
                        background: p.status === 'paid' ? '#2D5F4F' : 'white',
                        zIndex: 1,
                      }} />
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#6B6B6B', marginBottom: 4 }}>{p.date}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── PAY CARD SUMMARY (always shown) ── */}
          <div style={{ marginBottom: 30 }}>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, color: '#1B3B5F', marginBottom: 20 }}>
              💳 Pay Card
            </h2>

            {pc.isPlaceholder ? (
              /* ─── NOT YET ACTIVATED ─── */
              <div style={{
                background: 'linear-gradient(135deg, #1B3B5F 0%, #0D1F2D 100%)',
                color: 'white', borderRadius: 16, padding: 40,
                boxShadow: '0 8px 30px rgba(27,59,95,0.3)',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 40, alignItems: 'center' }}>
                  <div>
                    {/* card chip + header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
                      <div style={{ width: 44, height: 32, background: 'linear-gradient(135deg,#D4AF37,#C19A2E)', borderRadius: 6 }} />
                      <div>
                        <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1.5 }}>Hostel Pay Card</div>
                        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, opacity: 0.85 }}>
                          {detail.admissionNumber || '•••• ••••'}
                        </div>
                      </div>
                    </div>

                    <div style={{ fontSize: 13, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Current Balance</div>
                    <div style={{ fontSize: 52, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', lineHeight: 1, marginBottom: 22 }}>₹0.00</div>

                    {/* stats row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
                      {[
                        { label: 'Total Top-up', val: '₹0' },
                        { label: 'Total Spent',  val: '₹0' },
                        { label: 'Transactions', val: '0'  },
                      ].map((s, i) => (
                        <div key={i} style={{ borderLeft: '3px solid rgba(255,255,255,0.2)', paddingLeft: 14 }}>
                          <div style={{ fontSize: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{s.label}</div>
                          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif' }}>{s.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* action + status */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'flex-end' }}>
                    <button
                      onClick={() => onNavigate('payCardDetails', { payCardStudentId: detail.admissionId })}
                      style={{ padding: '14px 28px', background: '#D4AF37', color: '#1B3B5F', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => e.target.style.background = '#C19A2E'}
                      onMouseLeave={e => e.target.style.background = '#D4AF37'}>
                      💰 Activate &amp; Top-up
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.6, fontSize: 13 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#aaa' }} />
                      Card Not Yet Activated
                    </div>
                  </div>
                </div>

                {/* bottom banner */}
                <div style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', gap: 12, opacity: 0.75 }}>
                  <span style={{ fontSize: 18 }}>ℹ️</span>
                  <span style={{ fontSize: 13 }}>
                    This student doesn't have an active pay card yet. Top up for the first time to activate it automatically.
                  </span>
                </div>
              </div>

            ) : (
              /* ─── ACTIVE CARD ─── */
              <div style={{
                background: 'linear-gradient(135deg, #1B3B5F 0%, #0D1F2D 100%)',
                color: 'white', borderRadius: 16, padding: 40,
                boxShadow: '0 8px 30px rgba(27,59,95,0.3)',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 40, alignItems: 'center' }}>
                  <div>
                    {/* card chip + header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
                      <div style={{ width: 44, height: 32, background: 'linear-gradient(135deg,#D4AF37,#C19A2E)', borderRadius: 6 }} />
                      <div>
                        <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1.5 }}>Hostel Pay Card</div>
                        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, opacity: 0.85 }}>
                          {detail.admissionNumber}
                        </div>
                      </div>
                      {/* status badge */}
                      <div style={{
                        marginLeft: 'auto',
                        padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                        background: (pc.status === 'BLOCKED' || pc.status === 'blocked') ? 'rgba(220,53,69,0.25)' : (pc.status === 'INACTIVE' || pc.status === 'inactive') ? 'rgba(100,100,100,0.25)' : pc.balance < 2000 ? 'rgba(245,158,11,0.25)' : 'rgba(45,95,79,0.35)',
                        color:      (pc.status === 'BLOCKED' || pc.status === 'blocked') ? '#FF6B6B' : (pc.status === 'INACTIVE' || pc.status === 'inactive') ? '#C4BEB8' : pc.balance < 2000 ? '#FFD93D' : '#6EE7B7',
                        border:     `1px solid ${(pc.status === 'BLOCKED' || pc.status === 'blocked') ? 'rgba(220,53,69,0.4)' : (pc.status === 'INACTIVE' || pc.status === 'inactive') ? 'rgba(100,100,100,0.3)' : pc.balance < 2000 ? 'rgba(245,158,11,0.4)' : 'rgba(45,95,79,0.4)'}`,
                      }}>
                        {(pc.status === 'BLOCKED' || pc.status === 'blocked') ? '🚫 Suspended' : (pc.status === 'INACTIVE' || pc.status === 'inactive') ? '⏳ Fee Pending' : pc.balance < 2000 ? '⚠ Low Balance' : '✓ Active'}
                      </div>
                    </div>

                    <div style={{ fontSize: 13, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Current Balance</div>
                    <div style={{ fontSize: 52, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', lineHeight: 1, marginBottom: 22 }}>
                      ₹{Number(pc.balance).toLocaleString('en-IN')}
                    </div>

                    {/* stats row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
                      {[
                        { label: 'Total Top-up',  val: `₹${Number(pc.totalTopup).toLocaleString('en-IN')}` },
                        { label: 'Total Spent',   val: `₹${Number(pc.totalSpent).toLocaleString('en-IN')}` },
                        { label: 'Transactions',  val: String(pc.transactionCount) },
                      ].map((s, i) => (
                        <div key={i} style={{ borderLeft: '3px solid rgba(255,255,255,0.2)', paddingLeft: 14 }}>
                          <div style={{ fontSize: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{s.label}</div>
                          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif' }}>{s.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ✅ Actions — show QR only if ACTIVE, message if INACTIVE/BLOCKED */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
                    {(pc.status === 'INACTIVE' || pc.status === 'inactive') ? (
                      /* Fee not paid — QR hidden */
                      <div style={{ textAlign:'center', padding:'20px 16px', background:'rgba(0,0,0,0.2)', borderRadius:12, minWidth:160 }}>
                        <div style={{ fontSize:36, marginBottom:8 }}>⏳</div>
                        <div style={{ fontSize:12, fontWeight:700, color:'#FFD93D', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>Pay Card Fee Pending</div>
                        <div style={{ fontSize:11, opacity:0.7, lineHeight:1.4 }}>QR code will be activated once the prepaid card fee is paid</div>
                      </div>
                    ) : (pc.status === 'BLOCKED' || pc.status === 'blocked') ? (
                      /* Zero balance — QR disabled */
                      <div style={{ textAlign:'center', padding:'20px 16px', background:'rgba(220,53,69,0.15)', borderRadius:12, border:'1px solid rgba(220,53,69,0.3)', minWidth:160 }}>
                        <div style={{ fontSize:36, marginBottom:8 }}>🚫</div>
                        <div style={{ fontSize:12, fontWeight:700, color:'#FF6B6B', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>Card Suspended</div>
                        <div style={{ fontSize:11, opacity:0.7, lineHeight:1.4 }}>Balance is ₹0. Recharge wallet to reactivate QR</div>
                      </div>
                    ) : (
                      /* Active — show QR button */
                      <>
                        <button
                          onClick={() => onNavigate('payCardDetails', { payCardStudentId: detail.admissionId })}
                          style={{ padding: '14px 28px', background: '#D4AF37', color: '#1B3B5F', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          onMouseEnter={e => { e.target.style.background = '#C19A2E'; e.target.style.transform = 'translateY(-2px)'; }}
                          onMouseLeave={e => { e.target.style.background = '#D4AF37'; e.target.style.transform = 'none'; }}>
                          View Full Details
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => onNavigate('payCardDetails', { payCardStudentId: detail.admissionId })}
                      style={{ padding: '14px 28px', background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.4)', borderRadius: 8, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => { e.target.style.background = 'rgba(255,255,255,0.1)'; }}
                      onMouseLeave={e => { e.target.style.background = 'transparent'; }}>
                      💰 Top-up Card
                    </button>
                  </div>
                </div>

                {/* low balance warning */}
                {pc.balance < 2000 && pc.status !== 'blocked' && (
                  <div style={{ marginTop: 24, padding: '12px 18px', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                    <span>⚠️</span>
                    <span>Balance is below ₹2,000. Please top-up to avoid service interruption.</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── PAYMENT HISTORY ── */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <span style={{ fontSize: 24 }}>📋</span>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#1B3B5F', margin: 0 }}>Payment History</h2>
            </div>

            {!detail.history || detail.history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6B6B6B' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>No payments recorded yet</div>
                <div style={{ fontSize: 13 }}>Payments collected using the "Collect Payment" button will appear here.</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20 }}>
                <thead>
                  <tr style={{ background: '#F8F6F3', borderBottom: '2px solid #E8E4DF' }}>
                    {['Receipt No.', 'Fee Head', 'Date', 'Amount', 'Payment Mode', 'Transaction ID', 'Received By', 'Remark', 'Receipt'].map(h => (
                      <th key={h} style={{ padding: 12, textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.history.map((r, i) => (
                    <tr key={i} onMouseEnter={e => e.currentTarget.style.background = '#F8F6F3'} onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      {/* Receipt No */}
                      <td style={{ padding:'15px 12px', borderBottom:'1px solid #E8E4DF',
                        fontSize:12, fontWeight:700, color:'#1B3B5F', fontFamily:'monospace', whiteSpace:'nowrap' }}>
                        {r.receiptNo
                          ? r.receiptNo
                          : r.id
                            ? `OHR-${r.paymentDate ? r.paymentDate.slice(-4) : new Date().getFullYear()}-${String(r.id).padStart(6,'0')}`
                            : '—'}
                      </td>
                      {/* Fee Head */}
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 12 }}>
                        {(() => {
                          const m = (r.remarks || '').match(/^\[([^\]]+)\]/);
                          return m ? (
                            <span style={{ padding:'3px 8px', borderRadius:12, background:'#EEF3F9',
                              color:'#1B3B5F', fontWeight:700, fontSize:11, whiteSpace:'nowrap' }}>
                              {m[1]}
                            </span>
                          ) : <span style={{ color:'#9B9B9B' }}>—</span>;
                        })()}
                      </td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.paymentDate}</td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14, color: '#2D5F4F', fontWeight: 700 }}>₹{Number(r.amount).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.paymentMode}</td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.transactionId || '—'}</td>
                      <td style={{ padding: '15px 12px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.receivedBy}</td>

                      {/* Remark — the note part after the [Fee Head] prefix */}
                      <td style={{ padding:'15px 12px', borderBottom:'1px solid #E8E4DF', fontSize:13, color:'#6B6B6B', maxWidth:220 }}>
                        {(() => {
                          let note = (r.remarks || '').replace(/^\[[^\]]*\]\s*/, '').trim();
                          // System boilerplate is not a real remark — show a dash instead.
                          if (/^Initial payment at admission( \(migrated\))?$/i.test(note)) note = '';
                          return note ? note : <span style={{ color:'#9B9B9B' }}>—</span>;
                        })()}
                      </td>

                      {/* Download Receipt */}
                      <td style={{ padding:'12px', borderBottom:'1px solid #E8E4DF', textAlign:'center' }}>
                        <button
                          onClick={() => {
                            const rNo = r.receiptNo || (r.id ? `OHR-${r.paymentDate?r.paymentDate.slice(-4):new Date().getFullYear()}-${String(r.id).padStart(6,'0')}` : 'OHR-XXXX');
                            const m = (r.remarks||'').match(/^\[([^\]]+)\]/);
                            const feeHead = m ? m[1] : '—';
                            // Company heading based on fee head
                            const isFood  = feeHead.toLowerCase().includes('food');
                            const companyName = isFood ? 'Moonlight Trading Corporation'
                                                       : 'Omkara Impex and Merchandise LLP';
                            const companySub  = isFood ? 'Food & Catering Services'
                                                       : 'Hostel Management Services';
                            const amtNum = Number(r.amount);
                            const w = window.open('','_blank','width=900,height=1200');
                            if (w) {
                              w.document.write(`<!DOCTYPE html><html><head><title>Receipt ${rNo}</title>
                              <style>
                                *{margin:0;padding:0;box-sizing:border-box;}
                                body{font-family:'DM Sans',sans-serif;background:#f5f5f5;padding:20px;}
                                .receipt{width:794px;min-height:1123px;margin:0 auto;background:#fff;padding:60px;border:1px solid #ddd;position:relative;}
                                .top-header{text-align:center;border-bottom:3px solid #1B3B5F;padding-bottom:24px;margin-bottom:30px;}
                                .company-name{font-family:'Georgia',serif;font-size:30px;font-weight:700;color:#1B3B5F;letter-spacing:1px;}
                                .company-sub{font-size:12px;color:#6B6B6B;margin-top:4px;letter-spacing:2px;text-transform:uppercase;}
                                .receipt-title{display:inline-block;margin-top:16px;background:#1B3B5F;color:#D4AF37;padding:8px 36px;font-size:12px;font-weight:700;letter-spacing:3px;text-transform:uppercase;}
                                .meta-row{display:flex;justify-content:space-between;margin-bottom:30px;}
                                .meta-box{font-size:13px;color:#6B6B6B;line-height:2;}
                                .meta-box strong{color:#1B3B5F;font-weight:700;}
                                .divider{border:none;border-top:1px solid #e8e4df;margin:20px 0;}
                                .table{width:100%;border-collapse:collapse;margin-bottom:30px;}
                                .table th{background:#1B3B5F;color:#fff;padding:12px 16px;text-align:left;font-size:12px;letter-spacing:1px;text-transform:uppercase;}
                                .table td{padding:16px;border-bottom:1px solid #f0ede8;font-size:14px;color:#2C2C2C;}
                                .amount-box{background:#1B3B5F;color:#fff;padding:24px 32px;display:flex;justify-content:space-between;align-items:center;margin-bottom:40px;border-radius:4px;}
                                .amount-label{font-size:13px;letter-spacing:1px;text-transform:uppercase;opacity:0.8;}
                                .amount-value{font-size:38px;font-weight:700;color:#D4AF37;font-family:'Georgia',serif;}
                                .amount-words{font-size:12px;color:rgba(255,255,255,0.7);margin-top:4px;font-style:italic;}
                                .footer{position:absolute;bottom:60px;left:60px;right:60px;}
                                .sign-row{display:flex;justify-content:space-between;align-items:flex-end;padding-top:40px;border-top:1px solid #e8e4df;}
                                .sign-line{width:180px;border-top:1px solid #2C2C2C;margin-bottom:6px;}
                                .sign-label{font-size:11px;color:#6B6B6B;font-weight:700;letter-spacing:1px;text-transform:uppercase;}
                                .watermark{position:absolute;top:45%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);font-size:90px;color:rgba(27,59,95,0.04);font-family:'Georgia',serif;font-weight:700;pointer-events:none;white-space:nowrap;}
                                .foot-note{text-align:center;font-size:11px;color:#bbb;margin-top:16px;}
                                @media print{body{background:#fff;padding:0;}.receipt{border:none;min-height:auto;}button{display:none!important;}}
                              </style></head><body>
                              <div class="receipt">
                                <div class="watermark">RECEIPT</div>
                                <div class="top-header">
                                  <div class="company-name">${companyName}</div>
                                  <div class="company-sub">${companySub}</div>
                                  <div class="receipt-title">Payment Receipt</div>
                                </div>
                                <div class="meta-row">
                                  <div class="meta-box">
                                    <div><strong>Receipt No. : </strong>${rNo}</div>
                                    <div><strong>Date        : </strong>${r.paymentDate||'—'}</div>
                                    <div><strong>Payment Mode: </strong>${r.paymentMode||'—'}</div>
                                    ${r.transactionId ? `<div><strong>Transaction ID: </strong>${r.transactionId}</div>` : ''}
                                    <div><strong>Received By : </strong>${r.receivedBy||'Admin'}</div>
                                  </div>
                                  <div class="meta-box" style="text-align:right;">
                                    <div><strong>Student Name: </strong>${detail?.studentName||''}</div>
                                    <div><strong>Student ID  : </strong>${detail?.admissionNumber||'—'}</div>
                                    <div><strong>Mobile      : </strong>${detail?.mobile||'—'}</div>
                                  </div>
                                </div>
                                <hr class="divider"/>
                                <table class="table">
                                  <thead><tr><th>Description</th><th>Fee Head</th><th style="text-align:right;">Amount (₹)</th></tr></thead>
                                  <tbody>
                                    <tr>
                                      <td>Hostel Fee Payment — Academic Year 2026–27</td>
                                      <td>${feeHead}</td>
                                      <td style="text-align:right;font-weight:700;">₹${amtNum.toLocaleString('en-IN')}</td>
                                    </tr>
                                  </tbody>
                                </table>
                                <div class="amount-box">
                                  <div>
                                    <div class="amount-label">Total Amount Paid</div>
                                    <div class="amount-words">Rupees ${amtNum.toLocaleString('en-IN')} only</div>
                                  </div>
                                  <div class="amount-value">₹${amtNum.toLocaleString('en-IN')}</div>
                                </div>
                                <div class="footer">
                                  <div class="sign-row">
                                    <div>
                                      <div class="sign-line"></div>
                                      <div class="sign-label">Student Signature</div>
                                    </div>
                                    <div style="text-align:right;">
                                      <div class="sign-line" style="margin-left:auto;"></div>
                                      <div class="sign-label">Authorised Signatory</div>
                                      <div style="font-size:11px;color:#1B3B5F;margin-top:4px;font-weight:700;">${companyName}</div>
                                    </div>
                                  </div>
                                  <div class="foot-note">Computer-generated receipt · ${rNo}</div>
                                </div>
                              </div>
                              <div style="text-align:center;margin-top:20px;">
                                <button onclick="window.print()" style="padding:12px 36px;background:#1B3B5F;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;">🖨 Print / Download</button>
                              </div>
                              </body></html>`);
                              w.document.close();
                            }
                          }}
                          style={{ padding:'6px 12px', background:'#1B3B5F', color:'#fff',
                            border:'none', borderRadius:6, fontSize:11, fontWeight:700,
                            cursor:'pointer', whiteSpace:'nowrap' }}>
                          🧾 Receipt
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

        </div>
      </main>

      {showModal && (
        <CollectPaymentModal
          admissionId={detail.admissionId}
          studentName={detail.studentName}
          balance={detail.balance}
          detail={detail}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); loadDetail(); }}
        />
      )}
    </div>
  );
};

export default StudentFeeDetails;